import environment from 'environment';

import Rx from 'rxjs/Rx';
import CryptoJS from 'crypto-js';
import { hasSetupTransactionAuthentication, hasSetupAllTransactionAuthentication, isVerified } from './security.util';

const STORAGE_KEY = 'zimitech';

class SessionService {
  constructor(StorageService, SecurityApiService) {
    'ngInject';
    this.StorageService = StorageService;
    this.SecurityApiService = SecurityApiService;

    this.subject = new Rx.BehaviorSubject(undefined);
    this.session = this.subject.asObservable();
  }

  initialize() {
    this.loadSession();
  }

  login(payload) {
    return this.SecurityApiService.login(payload).then(response => this.processResponse(response));
  }

  logout() {
    return this.SecurityApiService.logout({}).then(() => this.clearSession());
  }

  processResponse(response) {
    let sessionDetails = {
      customerName: response.full_name,
      customerSince: response.joined_at,
      email: response.user.email,
      currency: response.currency.code === 'BTC' ? 'EUR' : response.currency.code,
      statuses: response.preferences.transaction.statuses,
      websocketToken: response.jwt_websocket_token,
      websocket: response.websocket,
      brokerageSyncId: response.brokerage_sync_id,
      tags: response.tags,
      zendeskId: response.zendesk_id,
      hasTransactionPasswordEnabled: response.has_enable_transaction_password,
      verifiedAt: response.verified_at,
      locale: response.locale,
      selectedAvatar: response.avatar ? response.avatar.selected : null,
      uploadedAvatar: response.avatar ? response.avatar.uploaded : null,
    };

    if (response.preferences.hasOwnProperty('google_authenticator')) {
      sessionDetails.hasTwoFactorEnabled = true;
      sessionDetails.isUsingGoogleTwoFactor = response.preferences.google_authenticator.is_default_2fa;
    } else {
      sessionDetails.hasTwoFactorEnabled = false;
      sessionDetails.isUsingGoogleTwoFactor = false;
    }


    if (response.preferences.restore_id) {
      this.StorageService.setItem('restore_id', response.preferences.restore_id);
    }

    let memberDetails = {
      'user_id': response.user.id,
      'full_name': response.full_name,
      'email': response.user.email,
      'country' : response.country.name,
      'birth_date' : response.birth_date
    };

    this.StorageService.setItem('locale', sessionDetails.locale);
    this.StorageService.setItem('member_details', JSON.stringify(memberDetails));
    this.StorageService.setItem('activation_code', response.user.activation_code);
    this.StorageService.setItem('show_activation_modal', true);
    this.saveSession(sessionDetails);

    return sessionDetails;
  }

  saveSession(dataToStore, triggerSubscribption = true) {
    let cipher = this.encrypt(JSON.stringify(dataToStore));
    this.StorageService.setItem(STORAGE_KEY, cipher);

    if (triggerSubscribption) {
      this.subject.next(dataToStore);
    }
  }

  clearSession() {
    window.fcWidget.user.clear();
    this.StorageService.removeAllCookie();
    this.StorageService.clear();
    this.StorageService.removeItem(STORAGE_KEY);
    this.subject.next(undefined);
  }

  getSession() {
    let cipher = this.StorageService.getItem(STORAGE_KEY);

    if (cipher) {
      let decrypted = this.decrypt(cipher);
      let session = JSON.parse(decrypted);
      session.hasSetupTransactionAuthentication = hasSetupTransactionAuthentication;
      session.hasSetupAllTransactionAuthentication = hasSetupAllTransactionAuthentication;
      session.isVerified = isVerified;

      return session;
    }

    return null;
  }

  // get an item from localstorage
  get(key) {
    let item = this.getSession()[key];

    return item;
  }

  set(key, value, triggerSubscription = true) {
    let data = this.getSession();
    data[key] = value;
    this.saveSession(data, triggerSubscription);
  }

  getLocale() {
    return this.StorageService.hasItem('locale') ? this.StorageService.getItem('locale') : 'en';
  }

  /**
   * Add AES encryption. Note that we do not store
   * any sensitive details on local storage.
   * There is no such secured encryption on front end
   * since private key will always be insecure.
   * We only do this, so that local storage content won't
   * be manually adjusted by any user. Though hacker
   * could try to decrypt it but again, our backend is secure and
   * we do not store sensitive details on localstorage. Also
   * note that our compiled source code will be jumbled making
   * it harder for anyone to decrypt our local storage.
   */
  encrypt(string) {
    return CryptoJS.AES.encrypt(string, environment.AES_ENCRYPTION_KEY);
  }

  decrypt(cipher) {
    let bytes = CryptoJS.AES.decrypt(cipher.toString(),environment.AES_ENCRYPTION_KEY);
    let encrypted = bytes.toString(CryptoJS.enc.Utf8);

    return encrypted;
  }

  loadSession() {
    try {
      this.subject.next(this.getSession());
    } catch (e) {
      // Try to reload session or just do the logout to avoid infinite loops.
    }
  }
}

export default SessionService;



// WEBPACK FOOTER //
// ./client/app/shared/security/session.service.js